import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router";
import type { DeliveryAddressCookieData } from "~/features/delivery-address/delivery-address.cookie";
import { VENDOR_PARAMS } from "~/utils/vendor";

/**
 * Syncs the `postalCode` URL search param with the delivery address cookie.
 * When the cookie has a postalCode and the URL does not, the URL is updated.
 * Runs once on mount to avoid overwriting user-initiated URL changes.
 */
export function useDeliveryAddressSync(
	deliveryAddress: DeliveryAddressCookieData | null,
): void {
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();

	useEffect(() => {
		if (location.pathname.startsWith("/profile")) {
			return;
		}
		if (!deliveryAddress?.postalCode) {
			return;
		}

		const currentPostalCode = searchParams.get(VENDOR_PARAMS.postalCode);
		if (currentPostalCode) {
			return;
		}

		setSearchParams(
			(prev) => {
				prev.set(VENDOR_PARAMS.postalCode, deliveryAddress.postalCode);
				return prev;
			},
			{ replace: true },
		);
	}, [
		deliveryAddress?.postalCode,
		location.pathname,
		searchParams,
		setSearchParams,
	]);
}
